import {
  buildBankFeedAnalysis,
  type BankBudgetReviewReason,
  type BankFeedAnalysis,
  type BankFeedTransactionInput,
  type BankBudgetCategoryId,
  type MoneyConfidence,
} from '../money';
import { buildWorkCashflowSnapshot, type WorkCashflowSnapshot } from './work';

export type WorkBusinessBankAccountInput = {
  id: string;
  institutionName?: string | null;
  accountName?: string | null;
  currency?: string | null;
  balance?: number | string | null;
  lastSynced?: string | null;
};

export type WorkBusinessBankTransactionInput = {
  transactionId?: string | null;
  accountId?: string | null;
  bookingDate: string;
  amount: number;
  currency?: string | null;
  description?: string | null;
  creditorName?: string | null;
  debtorName?: string | null;
  categoryId?: BankBudgetCategoryId | null;
  categoryLabel?: string | null;
  confidence?: MoneyConfidence;
  needsReview?: boolean;
  reviewReason?: BankBudgetReviewReason | null;
};

export type WorkBusinessBankProjection = {
  accountCount: number;
  accountLabels: string[];
  latestSync: string | null;
  currentMonthIncome: number;
  currentMonthSpend: number;
  recurringMonthlyRunningCost: number;
  reviewQueueCount: number;
  analysis: BankFeedAnalysis | null;
  cashflowSnapshot: WorkCashflowSnapshot | null;
};

function roundCurrency(value: number) {
  return Math.round(value * 100) / 100;
}

function buildAccountLabel(account: WorkBusinessBankAccountInput) {
  const pieces = [account.institutionName?.trim(), account.accountName?.trim()].filter(Boolean);
  return pieces.length ? pieces.join(' · ') : 'Connected business account';
}

export function buildWorkBusinessBankProjection({
  accounts,
  transactions,
}: {
  accounts: readonly WorkBusinessBankAccountInput[];
  transactions: readonly WorkBusinessBankTransactionInput[];
}): WorkBusinessBankProjection {
  const latestSync = accounts.reduce<string | null>((current, account) => {
    const candidate = account.lastSynced || null;
    if (!candidate) return current;
    if (!current) return candidate;
    return candidate > current ? candidate : current;
  }, null);

  const mappedTransactions: BankFeedTransactionInput[] = transactions.map((transaction) => ({
    transactionId: transaction.transactionId || null,
    accountId: transaction.accountId || null,
    bookingDate: transaction.bookingDate,
    amount: transaction.amount,
    currency: transaction.currency || null,
    description: transaction.description || null,
    merchantName: transaction.amount >= 0 ? transaction.debtorName || null : transaction.creditorName || null,
    categoryId: transaction.categoryId || null,
    categoryLabel: transaction.categoryLabel || null,
    confidence: transaction.confidence,
    needsReview: transaction.needsReview,
    reviewReason: transaction.reviewReason || null,
    source: 'bank',
  }));

  const analysis = mappedTransactions.length ? buildBankFeedAnalysis(mappedTransactions) : null;
  const currentMonthIncome = analysis?.budgetBreakdown.disposableIncome.income.monthlyActual || 0;
  const currentMonthSpend = analysis
    ? roundCurrency(analysis.currentMonthSpendingByCategory.reduce((sum, entry) => sum + entry.totalAmount, 0))
    : 0;
  const recurringMonthlyRunningCost = analysis
    ? roundCurrency(analysis.recurringOutgoings.reduce((sum, entry) => sum + entry.monthlyEquivalent, 0))
    : 0;
  const bankIncomeEntries = analysis
    ? analysis.normalisedTransactions
      .filter((entry) => entry.countsTowardsIncome)
      .map((entry) => ({
        amount: entry.absoluteAmount,
        receivedDate: entry.bookingDate,
      }))
    : [];
  const cashflowSnapshot = bankIncomeEntries.length
    ? buildWorkCashflowSnapshot({
      incomeEntries: bankIncomeEntries,
      invoices: [],
      monthlyRunningCost: recurringMonthlyRunningCost,
    })
    : null;

  return {
    accountCount: accounts.length,
    accountLabels: accounts.map((account) => buildAccountLabel(account)),
    latestSync,
    currentMonthIncome,
    currentMonthSpend,
    recurringMonthlyRunningCost,
    reviewQueueCount: analysis?.budgetBreakdown.reviewQueue.length || 0,
    analysis,
    cashflowSnapshot,
  };
}
